import { useEffect, useState } from "react";
import { api, formatApiError, API } from "@/lib/api";
import { toast } from "sonner";
import {
  DndContext, PointerSensor, useSensor, useSensors, closestCenter, DragOverlay, useDraggable,
} from "@dnd-kit/core";
import {
  SortableContext, useSortable, verticalListSortingStrategy, arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Plus, Trash, DotsSixVertical, ArrowUp, ArrowDown,
  TextAa, RadioButton, CheckSquare, CaretCircleDown, Hash, Star, Calendar, Envelope, Phone,
  TextT, Paperclip, TextHOne, Image as ImageIcon, VideoCamera, UploadSimple,
} from "@phosphor-icons/react";

export const PALETTE = [
  { type: "text", label: "Masukan Teks", icon: TextAa },
  { type: "radio", label: "Pilihan Ganda", icon: RadioButton },
  { type: "checkbox", label: "Kotak Centang", icon: CheckSquare },
  { type: "dropdown", label: "Tarik-Turun", icon: CaretCircleDown },
  { type: "number", label: "Number", icon: Hash },
  { type: "rating", label: "Peringkat", icon: Star },
  { type: "date", label: "Tanggal", icon: Calendar },
  { type: "email", label: "Email", icon: Envelope },
  { type: "tel", label: "Phone", icon: Phone },
  { type: "textarea", label: "Teks Panjang", icon: TextT },
  { type: "file", label: "Unggah File", icon: Paperclip },
  { type: "heading", label: "Judul", icon: TextHOne },
  { type: "image", label: "Gambar", icon: ImageIcon },
  { type: "video", label: "Video", icon: VideoCamera },
];

export const DISPLAY_ONLY = ["heading", "image", "video"];
export const HAS_OPTIONS = ["dropdown", "radio", "checkbox"];

export function genId() { return Math.random().toString(36).slice(2, 10); }

export function defaultFieldOfType(type) {
  const palette = PALETTE.find(p => p.type === type);
  const base = {
    id: genId(),
    type,
    label: palette?.label || "Pertanyaan",
    required: false,
    placeholder: "",
    options: [],
    content: "",
    media_url: "",
    max_rating: 5,
    conditional: null,
  };
  if (type === "heading") base.content = "Judul Bagian";
  if (HAS_OPTIONS.includes(type)) base.options = ["Opsi 1", "Opsi 2"];
  return base;
}

/** Evaluate whether a field should be visible based on its conditional and current answers. */
export function evalCondition(field, answers) {
  const c = field?.conditional;
  if (!c || !c.field_id) return true;
  const v = answers?.[c.field_id];
  const target = String(c.value ?? "").trim();
  const op = c.operator || "equals";
  if (Array.isArray(v)) {
    const has = v.map(x => String(x).trim()).includes(target);
    return op === "not_equals" ? !has : has;
  }
  const eq = String(v ?? "").trim() === target;
  return op === "not_equals" ? !eq : eq;
}

/**
 * Manage fields + routing_rules state with auto-sync on field changes.
 * @param {Object} value - {fields, routing_rules}
 * @param {Function} onChange - (next) => void
 */
export function useFieldsBuilder(value, onChange) {
  const fields = value.fields || [];
  const routing_rules = value.routing_rules || [];

  const updateField = (idx, patch) => {
    const copy = [...fields];
    const oldField = copy[idx];
    copy[idx] = { ...oldField, ...patch };
    const newField = copy[idx];

    let nextRules = routing_rules;
    let nextFields = copy;
    if (patch.options !== undefined || patch.type !== undefined) {
      // Sync routing rules
      nextRules = routing_rules
        .map(r => {
          if (r.field_id !== oldField.id) return r;
          if (!HAS_OPTIONS.includes(newField.type)) return { ...r, _invalid: true };
          const opts = newField.options || [];
          if (opts.length === 0) return { ...r, _invalid: true };
          if (!opts.includes(r.value)) return { ...r, value: opts[0] };
          return r;
        })
        .filter(r => !r._invalid);
      // Sync conditionals that reference this field
      nextFields = copy.map((f, i) => {
        if (i === idx) return f;
        const cond = f.conditional;
        if (!cond || cond.field_id !== oldField.id) return f;
        if (!HAS_OPTIONS.includes(newField.type)) return { ...f, conditional: null };
        const opts = newField.options || [];
        if (opts.length === 0) return { ...f, conditional: null };
        if (!opts.includes(cond.value)) return { ...f, conditional: { ...cond, value: opts[0] } };
        return f;
      });
    }
    onChange({ ...value, fields: nextFields, routing_rules: nextRules });
  };

  const removeField = (idx) => {
    const removed = fields[idx];
    const nextFields = fields
      .filter((_, i) => i !== idx)
      // Also drop conditionals that reference the removed field
      .map(f => (f.conditional?.field_id === removed?.id ? { ...f, conditional: null } : f));
    const nextRules = routing_rules.filter(r => r.field_id !== removed?.id);
    onChange({ ...value, fields: nextFields, routing_rules: nextRules });
  };

  const moveField = (idx, dir) => {
    const copy = [...fields];
    const j = idx + dir;
    if (j < 0 || j >= copy.length) return;
    [copy[idx], copy[j]] = [copy[j], copy[idx]];
    onChange({ ...value, fields: copy });
  };

  const addFieldByType = (type) => {
    onChange({ ...value, fields: [...fields, defaultFieldOfType(type)] });
  };

  const handleDragEnd = (event, activeDrag) => {
    const { active, over } = event;
    if (!over) return;
    const activeData = active.data.current;
    if (activeData?.fromPalette) {
      const newField = defaultFieldOfType(activeData.type);
      const overIdx = fields.findIndex(x => x.id === over.id);
      const copy = [...fields];
      if (overIdx === -1) copy.push(newField);
      else copy.splice(overIdx, 0, newField);
      onChange({ ...value, fields: copy });
    } else if (active.id !== over.id) {
      const oldIdx = fields.findIndex(x => x.id === active.id);
      const newIdx = fields.findIndex(x => x.id === over.id);
      if (oldIdx === -1 || newIdx === -1) return;
      onChange({ ...value, fields: arrayMove(fields, oldIdx, newIdx) });
    }
  };

  const setRoutingRules = (rules) => onChange({ ...value, routing_rules: rules });

  return { fields, routing_rules, updateField, removeField, moveField, addFieldByType, handleDragEnd, setRoutingRules };
}

export function FieldsDndProvider({ children, onDragEnd }) {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
  const [activeDrag, setActiveDrag] = useState(null);
  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={(e) => setActiveDrag(e.active.data.current)}
      onDragEnd={(e) => { onDragEnd(e, activeDrag); setActiveDrag(null); }}
    >
      {children}
      <DragOverlay>
        {activeDrag && (
          <div className="nb-btn nb-btn-secondary shadow-lg" data-testid="drag-overlay">
            {activeDrag.fromPalette ? <>{activeDrag.label || activeDrag.type}</> : <>Memindah field…</>}
          </div>
        )}
      </DragOverlay>
    </DndContext>
  );
}

export function FieldsPalette({ onAdd, title = "Palet Elemen", subtitle = "Seret atau klik" }) {
  return (
    <div className="nb-card p-4 sticky top-24">
      <span className="nb-badge bg-[#FFD166]">{title}</span>
      <h3 className="font-display font-bold text-base mt-2">{subtitle}</h3>
      <div className="space-y-2 mt-3 max-h-[60vh] overflow-y-auto pr-1">
        {PALETTE.map(p => (
          <PaletteItem key={p.type} item={p} onAdd={() => onAdd(p.type)} />
        ))}
      </div>
    </div>
  );
}

function PaletteItem({ item, onAdd }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `palette-${item.type}`,
    data: { fromPalette: true, type: item.type, label: item.label },
  });
  const Icon = item.icon;
  return (
    <div
      ref={setNodeRef}
      className={`flex items-center justify-between gap-2 p-2 border-2 border-[#121212] rounded-lg bg-white hover:bg-[#A7E4C0]/50 transition-colors ${isDragging ? "opacity-30" : ""}`}
      data-testid={`palette-${item.type}`}
    >
      <div className="flex items-center gap-2 flex-1 cursor-grab" {...listeners} {...attributes}>
        <Icon size={18} weight="bold" className="text-[#4A4A4A]" />
        <span className="text-sm font-medium">{item.label}</span>
      </div>
      <button
        type="button"
        onClick={onAdd}
        className="w-7 h-7 border-2 border-[#121212] rounded-md bg-white hover:bg-[#FFD166] flex items-center justify-center"
        data-testid={`palette-add-${item.type}`}
        title="Tambahkan ke kanvas"
      >
        <Plus size={14} weight="bold" />
      </button>
    </div>
  );
}

export function FieldsCanvas({ fields, updateField, removeField, moveField, emptyText = "Seret elemen dari palet ke sini" }) {
  return (
    <SortableContext items={fields.map(f => f.id)} strategy={verticalListSortingStrategy}>
      <div className="space-y-3 min-h-[200px]" data-testid="form-canvas">
        {fields.length === 0 && (
          <div className="nb-card p-10 text-center border-dashed">
            <p className="font-bold text-[#4A4A4A]">{emptyText}</p>
            <p className="text-xs text-[#4A4A4A] mt-1">atau klik tombol &ldquo;+&rdquo; pada palet untuk menambah</p>
          </div>
        )}
        {fields.map((fld, idx) => (
          <SortableFieldCard
            key={fld.id}
            field={fld}
            idx={idx}
            allFields={fields}
            onUpdate={(patch) => updateField(idx, patch)}
            onRemove={() => removeField(idx)}
            onMoveUp={() => moveField(idx, -1)}
            onMoveDown={() => moveField(idx, 1)}
          />
        ))}
      </div>
    </SortableContext>
  );
}

function SortableFieldCard({ field, idx, allFields, onUpdate, onRemove, onMoveUp, onMoveDown }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: field.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };
  const Icon = PALETTE.find(p => p.type === field.type)?.icon || TextAa;
  const isDisplay = DISPLAY_ONLY.includes(field.type);

  return (
    <div ref={setNodeRef} style={style} className="nb-card p-4" data-testid={`field-card-${idx}`}>
      <div className="flex items-start gap-3">
        <button
          {...attributes}
          {...listeners}
          className="cursor-grab text-[#4A4A4A] mt-2 touch-none"
          data-testid={`field-handle-${idx}`}
          aria-label="Geser untuk mengatur ulang"
        >
          <DotsSixVertical size={22} weight="bold" />
        </button>
        <div className="flex-1 space-y-2">
          <div className="flex items-center gap-2">
            <Icon size={16} weight="bold" />
            <span className="text-xs uppercase tracking-widest font-bold text-[#4A4A4A]">
              {PALETTE.find(p => p.type === field.type)?.label || field.type}
            </span>
            {isDisplay && <span className="nb-badge bg-[#CDB4DB] text-[10px]">Tampilan</span>}
          </div>
          <FieldEditor field={field} idx={idx} onUpdate={onUpdate} />
          <ConditionalEditor field={field} idx={idx} allFields={allFields} onUpdate={onUpdate} />
          <div className="flex items-center justify-between flex-wrap gap-2 pt-1">
            {!isDisplay ? (
              <label className="flex items-center gap-2 font-bold text-sm">
                <input type="checkbox" checked={!!field.required} onChange={e => onUpdate({ required: e.target.checked })} className="w-4 h-4 border-2 border-[#121212]" data-testid={`field-required-${idx}`} />
                Wajib diisi
              </label>
            ) : <span />}
            <div className="flex gap-1">
              <button onClick={onMoveUp} className="nb-btn !py-1 !px-2" data-testid={`field-up-${idx}`}><ArrowUp size={14} weight="bold" /></button>
              <button onClick={onMoveDown} className="nb-btn !py-1 !px-2" data-testid={`field-down-${idx}`}><ArrowDown size={14} weight="bold" /></button>
              <button onClick={onRemove} className="nb-btn !py-1 !px-2" data-testid={`field-remove-${idx}`}><Trash size={14} weight="bold" /></button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function FieldEditor({ field, idx, onUpdate }) {
  const t = field.type;

  if (t === "heading") {
    return (
      <div>
        <label className="nb-label">Teks Judul</label>
        <input className="nb-input" value={field.content || ""} onChange={e => onUpdate({ content: e.target.value })} placeholder="Bagian 1: Data Pribadi" data-testid={`field-content-${idx}`} />
        <label className="nb-label mt-2">Sub-deskripsi (opsional)</label>
        <input className="nb-input" value={field.placeholder || ""} onChange={e => onUpdate({ placeholder: e.target.value })} placeholder="Tampil di bawah judul" data-testid={`field-subdesc-${idx}`} />
      </div>
    );
  }
  if (t === "image") return <ImageFieldEditor field={field} idx={idx} onUpdate={onUpdate} />;
  if (t === "video") {
    return (
      <div>
        <label className="nb-label">URL Video (YouTube)</label>
        <input className="nb-input" value={field.media_url || ""} onChange={e => onUpdate({ media_url: e.target.value })} placeholder="https://youtube.com/watch?v=..." data-testid={`field-video-url-${idx}`} />
        <label className="nb-label mt-2">Caption (opsional)</label>
        <input className="nb-input" value={field.label || ""} onChange={e => onUpdate({ label: e.target.value })} data-testid={`field-video-caption-${idx}`} />
      </div>
    );
  }
  if (t === "rating") {
    return (
      <div className="space-y-2">
        <div>
          <label className="nb-label">Label Pertanyaan</label>
          <input className="nb-input" value={field.label} onChange={e => onUpdate({ label: e.target.value })} data-testid={`field-label-${idx}`} />
        </div>
        <div>
          <label className="nb-label">Maksimum Bintang</label>
          <select className="nb-input" value={field.max_rating || 5} onChange={e => onUpdate({ max_rating: Number(e.target.value) })} data-testid={`field-maxrating-${idx}`}>
            {[3, 5, 7, 10].map(n => <option key={n} value={n}>{n} bintang</option>)}
          </select>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div>
        <label className="nb-label">Label</label>
        <input className="nb-input" value={field.label} onChange={e => onUpdate({ label: e.target.value })} data-testid={`field-label-${idx}`} />
      </div>
      {!["dropdown", "radio", "checkbox", "file", "date"].includes(t) && (
        <div>
          <label className="nb-label">Placeholder</label>
          <input className="nb-input" value={field.placeholder || ""} onChange={e => onUpdate({ placeholder: e.target.value })} data-testid={`field-placeholder-${idx}`} />
        </div>
      )}
      {HAS_OPTIONS.includes(t) && (
        <div>
          <label className="nb-label">Opsi (pisahkan dengan koma)</label>
          <input className="nb-input"
            value={(field.options || []).join(", ")}
            onChange={e => onUpdate({ options: e.target.value.split(",").map(o => o.trim()).filter(Boolean) })}
            placeholder="Opsi 1, Opsi 2, Opsi 3"
            data-testid={`field-options-${idx}`}
          />
        </div>
      )}
    </div>
  );
}

function ConditionalEditor({ field, idx, allFields, onUpdate }) {
  const c = field.conditional;
  const enabled = !!c;
  // Eligible source fields: must have options AND come before this field
  const thisIdx = allFields.findIndex(x => x.id === field.id);
  const eligible = allFields.filter((f, i) => i < thisIdx && HAS_OPTIONS.includes(f.type) && (f.options || []).length > 0);

  const toggle = (on) => {
    if (!on) {
      onUpdate({ conditional: null });
      return;
    }
    const first = eligible[0];
    if (!first) return;
    onUpdate({ conditional: { field_id: first.id, operator: "equals", value: first.options[0] || "" } });
  };

  const update = (patch) => {
    const next = { ...(c || {}), ...patch };
    onUpdate({ conditional: next });
  };

  const sourceField = c ? allFields.find(x => x.id === c.field_id) : null;
  const valueValid = sourceField && (sourceField.options || []).includes(c?.value);
  const sourceExistsAndEligible = !!sourceField && HAS_OPTIONS.includes(sourceField.type);

  return (
    <div className="border-2 border-dashed border-[#CDB4DB] rounded-lg p-2 bg-[#CDB4DB]/10" data-testid={`field-conditional-${idx}`}>
      <label className="flex items-center gap-2 text-xs font-bold cursor-pointer">
        <input
          type="checkbox"
          checked={enabled}
          onChange={e => toggle(e.target.checked)}
          disabled={!enabled && eligible.length === 0}
          className="w-4 h-4 border-2 border-[#121212]"
          data-testid={`field-conditional-toggle-${idx}`}
        />
        <span>Tampil hanya jika…</span>
        {enabled && (!sourceExistsAndEligible || !valueValid) && (
          <span className="text-red-700 mono text-[10px]">⚠ kondisi tidak valid</span>
        )}
      </label>
      {!enabled && eligible.length === 0 && (
        <p className="text-[10px] text-[#4A4A4A] mt-1 italic">
          Butuh minimal 1 field bertipe Dropdown/Radio/Checkbox di atas field ini.
        </p>
      )}
      {enabled && (
        <div className="grid sm:grid-cols-12 gap-2 mt-2">
          <div className="sm:col-span-5">
            <select className="nb-input !py-1" value={c.field_id} onChange={e => {
              const f = allFields.find(x => x.id === e.target.value);
              update({ field_id: e.target.value, value: f?.options?.[0] || "" });
            }} data-testid={`field-conditional-source-${idx}`}>
              {!sourceExistsAndEligible && <option value={c.field_id}>(tidak valid)</option>}
              {eligible.map(f => <option key={f.id} value={f.id}>{f.label}</option>)}
            </select>
          </div>
          <div className="sm:col-span-3">
            <select className="nb-input !py-1" value={c.operator} onChange={e => update({ operator: e.target.value })} data-testid={`field-conditional-op-${idx}`}>
              <option value="equals">= sama dengan</option>
              <option value="not_equals">≠ tidak sama</option>
            </select>
          </div>
          <div className="sm:col-span-4">
            <select className="nb-input !py-1" value={c.value} onChange={e => update({ value: e.target.value })} data-testid={`field-conditional-value-${idx}`}>
              {!valueValid && c.value && <option value={c.value}>(tidak valid: {c.value})</option>}
              {(sourceField?.options || []).map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>
        </div>
      )}
    </div>
  );
}

function ImageFieldEditor({ field, idx, onUpdate }) {
  const [uploading, setUploading] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onUpdate({ media_url: `${API}/files/${data.path}` });
      toast.success("Gambar diunggah");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Gagal upload");
    } finally {
      setUploading(false);
    }
  };
  return (
    <div className="space-y-2">
      <label className="nb-label">URL Gambar</label>
      <input className="nb-input" value={field.media_url || ""} onChange={e => onUpdate({ media_url: e.target.value })} placeholder="https://... atau unggah" data-testid={`field-image-url-${idx}`} />
      <label className="nb-btn nb-btn-secondary cursor-pointer w-full justify-center !py-2">
        <UploadSimple size={16} weight="bold" /> {uploading ? "Mengunggah…" : "Unggah dari komputer"}
        <input type="file" accept="image/*" className="hidden" onChange={e => upload(e.target.files?.[0])} data-testid={`field-image-upload-${idx}`} />
      </label>
      {field.media_url && (
        <img src={field.media_url} alt="" className="w-full h-32 object-cover border-2 border-[#121212] rounded-lg" />
      )}
      <label className="nb-label">Caption (opsional)</label>
      <input className="nb-input" value={field.label || ""} onChange={e => onUpdate({ label: e.target.value })} data-testid={`field-image-caption-${idx}`} />
    </div>
  );
}

export function RoutingRulesEditor({ fields, rules, onChange }) {
  const eligibleFields = fields.filter(f => HAS_OPTIONS.includes(f.type) && (f.options || []).length > 0);
  const [today, setToday] = useState(null);

  useEffect(() => {
    api.get("/holidays/today").then(r => setToday(r.data)).catch(() => {});
  }, []);

  const addAnswerRule = () => {
    const first = eligibleFields[0];
    if (!first) return;
    onChange([...rules, {
      id: genId(),
      condition_type: "answer",
      field_id: first.id,
      value: first.options[0] || "",
      whatsapp_number: "",
      label: "",
    }]);
  };

  const addHolidayRule = () => {
    onChange([...rules, {
      id: genId(),
      condition_type: "holiday",
      field_id: "",
      value: "",
      whatsapp_number: "",
      label: "Piket Hari Libur",
    }]);
  };

  const updateRule = (idx, patch) => {
    const copy = [...rules];
    copy[idx] = { ...copy[idx], ...patch };
    onChange(copy);
  };

  const removeRule = (idx) => onChange(rules.filter((_, i) => i !== idx));

  return (
    <div className="nb-card p-5 bg-[#CDB4DB]/30" data-testid="routing-rules-card">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <span className="nb-badge bg-[#CDB4DB]">Tim & Routing WA</span>
          <h3 className="font-display font-bold text-lg mt-2">Distribusi ke nomor WA tim</h3>
          <p className="text-xs text-[#4A4A4A] mt-1">Atur agar pesanan/submission tertentu dikirim ke nomor WA staf yang berbeda.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={addAnswerRule} disabled={eligibleFields.length === 0} className="nb-btn" data-testid="routing-add-rule-button">
            <Plus size={16} weight="bold" /> Aturan Jawaban
          </button>
          <button onClick={addHolidayRule} className="nb-btn nb-btn-secondary" data-testid="routing-add-holiday-rule-button">
            <Plus size={16} weight="bold" /> Aturan Hari Libur
          </button>
        </div>
      </div>

      {eligibleFields.length === 0 && (
        <p className="text-sm text-[#4A4A4A] mt-3 italic">
          Untuk aturan berdasarkan jawaban: tambah dulu field tipe <strong>Dropdown / Radio / Checkbox</strong> dengan opsi.
        </p>
      )}

      {today && (
        <div className={`mt-3 text-xs px-3 py-2 rounded-lg border-2 ${today.is_holiday ? "bg-[#FFD166] border-[#121212]" : "bg-white border-[#121212]/40"}`} data-testid="today-status">
          <strong>Hari ini:</strong> {new Date(today.date).toLocaleDateString("id-ID", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
          {today.is_holiday
            ? <span> — 🇮🇩 <strong>Hari Libur: {today.name}</strong> (aturan hari libur akan aktif)</span>
            : <span> — hari kerja biasa.</span>}
        </div>
      )}

      {rules.length > 0 && (
        <div className="space-y-3 mt-4">
          {rules.map((r, idx) => (
            <RoutingRuleCard
              key={r.id}
              rule={r}
              idx={idx}
              fields={fields}
              eligibleFields={eligibleFields}
              onUpdate={(p) => updateRule(idx, p)}
              onRemove={() => removeRule(idx)}
            />
          ))}
          <p className="text-xs text-[#4A4A4A]">
            Aturan diperiksa berurutan; pertama yang cocok menang. Kalau tidak ada yang cocok, pesan dikirim ke nomor WA default.
          </p>
        </div>
      )}
    </div>
  );
}

function RoutingRuleCard({ rule: r, idx, fields, eligibleFields, onUpdate, onRemove }) {
  const isHoliday = r.condition_type === "holiday";
  const waValid = (r.whatsapp_number || "").replace(/\D/g, "").length >= 8;

  if (isHoliday) {
    return (
      <div className={`border-2 rounded-lg p-3 bg-white ${waValid ? "border-[#121212]" : "border-red-600"}`} data-testid={`routing-rule-${idx}`}>
        <div className="flex items-center gap-2 mb-2">
          <span className="nb-badge bg-[#FFD166] text-[10px]" data-testid={`routing-rule-${idx}-type`}>🇮🇩 Hari Libur</span>
          <span className="text-xs text-[#4A4A4A]">Aktif otomatis pada hari Minggu, tanggal merah nasional, dan tanggal libur manual (menu Pengaturan).</span>
        </div>
        {!waValid && (
          <div className="mb-2 text-xs font-bold text-red-700 bg-red-50 border border-red-300 rounded px-2 py-1" data-testid={`routing-rule-${idx}-warning`}>
            ⚠ Nomor WA tidak valid (min 8 digit).
          </div>
        )}
        <div className="grid sm:grid-cols-12 gap-2">
          <div className="sm:col-span-6">
            <label className="nb-label">Kirim ke No. WA</label>
            <input className="nb-input" value={r.whatsapp_number} onChange={e => onUpdate({ whatsapp_number: e.target.value })} placeholder="628..." data-testid={`routing-wa-${idx}`} />
          </div>
          <div className="sm:col-span-6">
            <label className="nb-label">Label Tim (opsional)</label>
            <input className="nb-input" value={r.label || ""} onChange={e => onUpdate({ label: e.target.value })} placeholder="Piket Hari Libur" data-testid={`routing-label-${idx}`} />
          </div>
        </div>
        <div className="flex justify-end mt-2">
          <button onClick={onRemove} className="nb-btn !py-1 !px-2" data-testid={`routing-remove-${idx}`}><Trash size={14} weight="bold" /></button>
        </div>
      </div>
    );
  }

  // Answer-based rule
  const f = fields.find(x => x.id === r.field_id);
  const fieldExists = !!f && HAS_OPTIONS.includes(f.type);
  const valueValid = fieldExists && (f.options || []).includes(r.value);
  const isValid = fieldExists && valueValid && waValid;

  return (
    <div className={`border-2 rounded-lg p-3 bg-white ${isValid ? "border-[#121212]" : "border-red-600"}`} data-testid={`routing-rule-${idx}`}>
      <div className="flex items-center gap-2 mb-2">
        <span className="nb-badge bg-[#A7E4C0] text-[10px]" data-testid={`routing-rule-${idx}-type`}>💬 Jawaban Field</span>
      </div>
      {!isValid && (
        <div className="mb-2 text-xs font-bold text-red-700 bg-red-50 border border-red-300 rounded px-2 py-1" data-testid={`routing-rule-${idx}-warning`}>
          ⚠ {!fieldExists ? "Field tidak ditemukan." : !valueValid ? "Nilai tidak ada di opsi field saat ini." : !waValid ? "Nomor WA tidak valid (min 8 digit)." : ""}
        </div>
      )}
      <div className="grid sm:grid-cols-12 gap-2">
        <div className="sm:col-span-4">
          <label className="nb-label">Jika field</label>
          <select className="nb-input" value={r.field_id} onChange={e => {
            const newF = fields.find(x => x.id === e.target.value);
            onUpdate({ field_id: e.target.value, value: newF?.options?.[0] || "" });
          }} data-testid={`routing-field-${idx}`}>
            {!fieldExists && <option value={r.field_id}>(field dihapus)</option>}
            {eligibleFields.map(ff => <option key={ff.id} value={ff.id}>{ff.label}</option>)}
          </select>
        </div>
        <div className="sm:col-span-3">
          <label className="nb-label">Bernilai</label>
          <select className="nb-input" value={r.value} onChange={e => onUpdate({ value: e.target.value })} data-testid={`routing-value-${idx}`}>
            {!valueValid && r.value && <option value={r.value}>(tidak valid: {r.value})</option>}
            {(f?.options || []).map(o => <option key={o} value={o}>{o}</option>)}
          </select>
        </div>
        <div className="sm:col-span-5">
          <label className="nb-label">Kirim ke No. WA</label>
          <input className="nb-input" value={r.whatsapp_number} onChange={e => onUpdate({ whatsapp_number: e.target.value })} placeholder="628..." data-testid={`routing-wa-${idx}`} />
        </div>
      </div>
      <div className="flex gap-2 mt-2">
        <input className="nb-input flex-1" value={r.label || ""} onChange={e => onUpdate({ label: e.target.value })} placeholder="Label tim (mis. Staf Jakarta)" data-testid={`routing-label-${idx}`} />
        <button onClick={onRemove} className="nb-btn" data-testid={`routing-remove-${idx}`}><Trash size={14} weight="bold" /></button>
      </div>
    </div>
  );
}

/** Render a single field as input on the public form/menu page. */
export function PublicFieldRenderer({ field, value, onChange, uploadFile, uploaded, ytEmbed }) {
  const f = field;

  if (f.type === "heading") {
    return (
      <div className="pt-3" data-testid={`pub-heading-${f.id}`}>
        <h2 className="font-display font-bold text-xl tracking-tight">{f.content || "Bagian"}</h2>
        {f.placeholder && <p className="text-sm text-[#4A4A4A] mt-1">{f.placeholder}</p>}
        <div className="nb-divider mt-2 opacity-30" />
      </div>
    );
  }
  if (f.type === "image") {
    if (!f.media_url) return null;
    return (
      <div data-testid={`pub-image-${f.id}`}>
        <img src={f.media_url} alt={f.label || ""} className="w-full rounded-lg border-2 border-[#121212] object-cover max-h-80" />
        {f.label && <p className="text-xs text-[#4A4A4A] mt-1 italic">{f.label}</p>}
      </div>
    );
  }
  if (f.type === "video") {
    const embed = ytEmbed?.(f.media_url);
    if (!embed) return null;
    return (
      <div data-testid={`pub-video-${f.id}`}>
        <div className="aspect-video w-full border-2 border-[#121212] rounded-lg overflow-hidden">
          <iframe src={embed} title={f.label || "video"} className="w-full h-full" allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
        </div>
        {f.label && <p className="text-xs text-[#4A4A4A] mt-1 italic">{f.label}</p>}
      </div>
    );
  }

  return (
    <div>
      <label className="nb-label">
        {f.label} {f.required && <span className="text-red-600">*</span>}
      </label>
      {renderInputControl(f, value, onChange, uploadFile, uploaded)}
    </div>
  );
}

function renderInputControl(f, val, setVal, uploadFile, uploaded) {
  const common = { className: "nb-input", placeholder: f.placeholder || "" };
  if (f.type === "textarea") return <textarea rows={3} {...common} value={val || ""} onChange={e => setVal(e.target.value)} data-testid={`pub-field-${f.id}`} />;
  if (f.type === "dropdown") return (
    <select {...common} value={val || ""} onChange={e => setVal(e.target.value)} data-testid={`pub-field-${f.id}`}>
      <option value="">Pilih…</option>
      {f.options?.map(o => <option key={o} value={o}>{o}</option>)}
    </select>
  );
  if (f.type === "radio") return (
    <div className="space-y-2">
      {f.options?.map(o => (
        <label key={o} className="flex items-center gap-2 cursor-pointer">
          <input type="radio" name={f.id} value={o} checked={val === o} onChange={e => setVal(e.target.value)} className="w-4 h-4" data-testid={`pub-field-${f.id}-${o}`} />
          {o}
        </label>
      ))}
    </div>
  );
  if (f.type === "checkbox") return (
    <div className="space-y-2">
      {f.options?.map(o => (
        <label key={o} className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={Array.isArray(val) && val.includes(o)}
            onChange={e => {
              const arr = Array.isArray(val) ? [...val] : [];
              if (e.target.checked) arr.push(o); else arr.splice(arr.indexOf(o), 1);
              setVal(arr);
            }}
            className="w-4 h-4"
            data-testid={`pub-field-${f.id}-${o}`}
          />
          {o}
        </label>
      ))}
    </div>
  );
  if (f.type === "rating") {
    const max = f.max_rating || 5;
    return (
      <div className="flex gap-1" data-testid={`pub-field-${f.id}`}>
        {Array.from({ length: max }).map((_, i) => {
          const v = i + 1;
          const active = (val || 0) >= v;
          return (
            <button
              type="button"
              key={v}
              onClick={() => setVal(val === v ? 0 : v)}
              className="transition-transform hover:scale-110"
              data-testid={`pub-rating-${f.id}-${v}`}
              aria-label={`${v} bintang`}
            >
              <Star size={32} weight={active ? "fill" : "regular"} className={active ? "text-[#FFD166]" : "text-[#4A4A4A]"} />
            </button>
          );
        })}
      </div>
    );
  }
  if (f.type === "file") return (
    <label className="nb-btn nb-btn-secondary cursor-pointer w-full justify-center">
      <Paperclip size={16} weight="bold" /> {uploaded ? `✓ ${uploaded.name}` : "Pilih file"}
      <input type="file" className="hidden" onChange={e => uploadFile?.(f.id, e.target.files?.[0])} data-testid={`pub-field-${f.id}`} />
    </label>
  );
  if (f.type === "date") return <input type="date" {...common} value={val || ""} onChange={e => setVal(e.target.value)} data-testid={`pub-field-${f.id}`} />;
  if (f.type === "number") return <input type="number" {...common} value={val || ""} onChange={e => setVal(e.target.value)} data-testid={`pub-field-${f.id}`} />;
  if (f.type === "email") return <input type="email" {...common} value={val || ""} onChange={e => setVal(e.target.value)} data-testid={`pub-field-${f.id}`} />;
  if (f.type === "tel") return <input type="tel" {...common} value={val || ""} onChange={e => setVal(e.target.value)} data-testid={`pub-field-${f.id}`} />;
  return <input type="text" {...common} value={val || ""} onChange={e => setVal(e.target.value)} data-testid={`pub-field-${f.id}`} />;
}

export function ytEmbed(url) {
  if (!url) return null;
  try {
    const u = new URL(url);
    let id = null;
    if (u.hostname.includes("youtu.be")) id = u.pathname.slice(1);
    else if (u.searchParams.get("v")) id = u.searchParams.get("v");
    else if (u.pathname.startsWith("/embed/")) id = u.pathname.split("/")[2];
    if (!id) return null;
    return `https://www.youtube.com/embed/${id}`;
  } catch {
    return null;
  }
}
